import type {
  AbstractSingleFilterPillsProps,
  KeywordResponseFilterValue,
} from '@hotjar/global-filters';
import {
  RadioTextFilterOperation,
  GenericRadioTextFilter,
  readableRadioTextOperations,
} from '@hotjar/global-filters';
import { useTranslation } from '@hotjar/translations';
import { useCallback } from 'react';

interface KeywordFilterProps extends AbstractSingleFilterPillsProps<KeywordResponseFilterValue> {
  isDisabled?: boolean;
  disabledReason?: string;
}

export function KeywordFilter({
  currentValue,
  onApply,
  onRemove,
  onDropdownHidden,
  showDropdownOnMount,
  isInactive,
  isDisabled,
  disabledReason,
}: KeywordFilterProps): JSX.Element {
  const { t } = useTranslation('feedback', { keyPrefix: 'survey-response.filters.keyword' });

  const placeholders = {
    [RadioTextFilterOperation.CONTAINS]: t('contains-placeholder'),
    [RadioTextFilterOperation.NOT_CONTAINS]: t('not-contains-placeholder'),
  };

  const onApplyWrapper = useCallback(
    (value) => {
      switch (value?.operation) {
        case RadioTextFilterOperation.CONTAINS:
        case RadioTextFilterOperation.NOT_CONTAINS:
          onApply(value);
          return;
      }
      onApply(undefined);
    },
    [onApply],
  );

  return (
    <GenericRadioTextFilter
      filterName={t('keyword')}
      iconName="string"
      currentValue={currentValue}
      onApply={onApplyWrapper}
      onRemove={onRemove}
      placeholders={placeholders}
      options={[RadioTextFilterOperation.CONTAINS, RadioTextFilterOperation.NOT_CONTAINS]}
      onDropdownHidden={onDropdownHidden}
      showDropdownOnMount={showDropdownOnMount}
      isInactive={isInactive}
      isDisabled={isDisabled}
      disabledReason={disabledReason}
      showDropdownCaret
      label1={t('keyword')}
      label2={
        currentValue && 'value' in currentValue
          ? `${readableRadioTextOperations[currentValue.operation]} "${currentValue.value}"`
          : ''
      }
    />
  );
}
